import * as React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Text } from '@wix/design-system';
import { ChevronUp } from '@wix/wix-ui-icons-common';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useBreakpoint } from 'providers/BreakpointProvider';
import { Collapse } from '@wix/package-picker-components';
import { FaqAnswer } from './FaqAnswer';
import { Experiments, MediaDevices } from 'enums';
import s from './Faq.scss';

export interface FaqItemProps {
  faqCode: string;
  isEnterpriseFaqs: boolean;
  rootStore: RootStore;
}

const FaqItemCmp: React.FC<FaqItemProps> = ({
  faqCode,
  isEnterpriseFaqs,
  rootStore,
}) => {
  const { currentBreakpoint } = useBreakpoint();
  const isMobile = currentBreakpoint === MediaDevices.Mobile;
  const isMobileTrustEnabled = rootStore.appStore.experiments[Experiments.MobileTrust] === 'true';
  const isCompact = isMobile && isMobileTrustEnabled;
  const questionKey = isEnterpriseFaqs
    ? `enterprise_plans.faq.question.${faqCode}`
    : `${faqCode}.question`;

  const toggleBtnKey = `toggle-faq-${faqCode}`;
  const {
    appContext: { biLogger, siteGuid, locale, userGuid, clientId },
    planType,
  } = rootStore.appStore;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const toggleOpen = () => {
    setIsOpen(!isOpen);
    biLogger.logClickOnFAQItem({
      ppType: planType,
      clickType: isOpen ? 'hide' : 'show',
      questionType: questionKey,
      siteGuid,
    });
  };
  const onHoverOn = () => setIsHovered(true);
  const onHoverOff = () => setIsHovered(false);
  const { t } = useTranslation();
  const { theme } = usePageModeContext();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOpen();
    }
  };

  return (
    <div
      className={s.faqItemContainer}
      onClick={toggleOpen}
      onMouseOut={onHoverOff}
      onMouseOver={onHoverOn}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-expanded={isOpen}
    >
      <div className={s.faqItemInnerContainer}>
        <div className={s.questionTitle}>
          <Text
            weight='bold'
            size={isCompact ? 'small' : 'medium'}
            className={classNames(s.question, theme)}
            tagName='h2'
            dataHook='faq-question'
          >
            {t(questionKey)}
          </Text>
          <div
            data-testid={toggleBtnKey}
            onClick={toggleOpen}
            className={classNames(s.icon, {
              [s.hovered]: isHovered,
              [s.open]: isOpen,
            })}
          >
            <ChevronUp size='36px' className={isOpen ? s.open : s.closed} />
          </div>
        </div>
        <Collapse isOpened={isOpen}>
          <FaqAnswer
            faqCode={faqCode}
            isEnterpriseFaqs={isEnterpriseFaqs}
            locale={locale}
            userGuid={userGuid}
            clientId={clientId}
            isOpen={isOpen}
            isCompact={isCompact}
          />
        </Collapse>
      </div>
    </div>
  );
};

export const FaqItem = withInjectedRootStore(FaqItemCmp);
